import { Injectable } from '@angular/core';
import { ZendeskCommands } from '../shared/enums/zendesk-commands';
import { User } from '../modules/users/models/user';

declare global {
  interface Window {
    zE: {
      (command: string, action?: string, ...params: any[]): any;
      (command: string, ...args: any[]): any;
    };
    zESettings: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ZendeskService {
  private _isInitialized = false;
  private loadPromise?: Promise<boolean>;

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  async initZendesk(settings?: any): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initWidget();
    }

    return new Promise(resolve => {
      window.zESettings = settings;
      resolve(true);
    });
  }

  setContactFormData(user: User | null): void {
    if (!this.isInitialized) {
      return;
    }

    if (user === null) {
      this.executeZendeskCommand(ZendeskCommands.WidgetType, ZendeskCommands.Reset);

      return;
    }

    const data = { name: { value: `${user.FirstName} ${user.LastName}` }, email: { value: user.Email } };

    this.executeZendeskCommand(ZendeskCommands.WidgetType, ZendeskCommands.Prefill, data);
  }

  /*Need for future feat. On some pages the button will be hide*/

  hideWidget(): void {
    if (!this.isInitialized) {
      return;
    }

    this.executeZendeskCommand(ZendeskCommands.WidgetType, ZendeskCommands.Hide);
  }

  showWidget(): void {
    const widgetState = window.zE(ZendeskCommands.WidgetGet, ZendeskCommands.Display);

    if (!this.isInitialized || widgetState !== 'hidden') {
      return;
    }

    this.executeZendeskCommand(ZendeskCommands.WidgetType, ZendeskCommands.Show);
  }

  /*Need for localization in future*/

  setWidgetLanguage(language: string): void {
    if (!this.isInitialized) {
      return;
    }

    this.executeZendeskCommand(ZendeskCommands.WidgetType, ZendeskCommands.SetLocale, language);
  }

  private executeZendeskCommand(command: string, action?: string, ...params: any[]): void {
    if (!this.isInitialized) {
      console.error('Zendesk not loaded yet');
      return;
    }

    try {
      if (action) {
        window.zE(command, action, ...params);
      } else {
        window.zE(command, ...params);
      }
    } catch (error) {
      console.error('Zendesk command failed:', error);
    }
  }

  private initWidget(): Promise<boolean> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      const nonce = (document.querySelector('meta[name="CSP_NONCE"]') as HTMLMetaElement)?.getAttribute('content');

      const script = document.createElement('script');
      script.onload = () => {
        this._isInitialized = true;
        resolve(true);
      };
      script.onerror = error => reject(new Error(`Failed to load widget: ${error}`));
      script.id = 'ze-snippet';
      script.src = 'https://static.zdassets.com/ekr/snippet.js?key=8af33c0d-39c1-452c-b375-39724bd66437';
      if (nonce) {
        script.setAttribute('nonce', nonce);
      }
      script.async = true;
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }
}
